import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Button,
  ButtonGroup,
  Pagination,
  Snackbar,
} from '@mui/material';
import { apiService, getApiErrorMessage } from '../services/apiService';
import DetailPanel from './DetailPanel';

const isDateFieldKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  return normalizedKey.includes('datum')
    || normalizedKey === 'projektkezdete'
    || normalizedKey === 'tervezettbefejezes';
};

const DETAIL_ONLY_HIDDEN_KEYS = new Set([
  'id',
  'megrendeloId',
  'ugyintezoId',
  'torzsszam',
  'ev',
  'sorszam',
]);

interface MainContentProps {
  selectedMenu: string | null;
  searchFilter: {column: string, value: string} | null;
  onColumnsUpdate: (columns: string[]) => void;
  visibleColumns?: string[] | null;
  refreshTrigger?: number;
  onDataUpdate?: (data: any[]) => void;
  editPermission?: string;
  deletePermission?: string;
  onNavigateToParticipantTimeTracking?: (participantName: string, munkaszam: string) => void;
  onNavigateToFolyamatlap?: (munkaszam: string) => void;
}

const MainContent: React.FC<MainContentProps> = ({ 
  selectedMenu, 
  searchFilter, 
  onColumnsUpdate, 
  visibleColumns,
  refreshTrigger,
  onDataUpdate,
  editPermission,
  deletePermission,
  onNavigateToParticipantTimeTracking,
  onNavigateToFolyamatlap
}) => {
  const padLeft = (input: number | string, length: number): string => {
    const text = String(input);
    if (text.length >= length) {
      return text;
    }

    return `${new Array(length - text.length + 1).join('0')}${text}`;
  };

  const collectObjectValues = (source: any): any[] => {
    if (!source) {
      return [];
    }

    return Object.keys(source).map((key) => source[key]);
  };

  const collectAllColumns = (rows: any[]): string[] => {
    const seen: { [key: string]: boolean } = {};
    const columns: string[] = [];

    rows.forEach((item: any) => {
      Object.keys(item || {}).forEach((key) => {
        if (!seen[key]) {
          seen[key] = true;
          columns.push(key);
        }
      });
    });

    return columns;
  };

  const hasColumn = (columns: string[], key: string): boolean => columns.indexOf(key) !== -1;

  const [data, setData] = useState<any[]>([]);
  const [filteredData, setFilteredData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info' | 'warning';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  useEffect(() => {
    if (selectedMenu) {
      setCurrentPage(1); // Reset to first page when menu changes
      fetchData(selectedMenu);
    }
  }, [selectedMenu, refreshTrigger]); // Add refreshTrigger to dependency array

  // Apply search filter when data or searchFilter changes
  useEffect(() => {
    if (!searchFilter || !searchFilter.value) {
      setFilteredData(data);
    } else {
      const filtered = data.filter(item => {
        // If column is empty, search in all columns
        if (!searchFilter.column || searchFilter.column === '') {
          return collectObjectValues(item).some((value: any) => {
            if (value == null) return false;
            return value.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
          });
        }
        
        // Otherwise search in specific column
        const cellValue = item[searchFilter.column];
        if (cellValue == null) return false;
        return cellValue.toString().toLowerCase().includes(searchFilter.value.toLowerCase());
      });
      setFilteredData(filtered);
    }
    setCurrentPage(1); // Reset to first page when filter changes
  }, [data, searchFilter]);

  // Update columns when data changes
  useEffect(() => {
    if (data.length > 0) {
      if (onColumnsUpdate) {
        const columns = collectAllColumns(data);
        onColumnsUpdate(columns);
      }
      if (onDataUpdate) {
        onDataUpdate(data);
      }
    }
  }, [data, onColumnsUpdate, onDataUpdate]);

  const fetchData = async (menu: string) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiService.getData(menu);
      setData(result);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Hiba történt az adatok betöltése során.'));
    } finally {
      setLoading(false);
    }
  };

  const getMenuTitle = (menu: string) => {
    const titles: { [key: string]: string } = {
      ajanlatok: 'Ajánlatok',
      idoraforditasok: 'Időráfordítások',
      iktatas: 'Iktatások',
      megrendelok: 'Megrendelők',
      munkaszamok: 'Munkaszámok',
    };
    return titles[menu] || menu;
  };

  const handleRecordsPerPageChange = (newRecordsPerPage: number) => {
    setRecordsPerPage(newRecordsPerPage);
    setCurrentPage(1); // Reset to first page when changing records per page
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const handleRowClick = async (row: any) => {
    if (!row?.id) {
      setSelectedItem(row);
      setIsPanelOpen(true);
      return;
    }

    const editableEndpointMap: { [key: string]: string } = {
      ajanlatok: 'Ajanlatok',
      idoraforditasok: 'Idoraforditasok',
      iktatas: 'Iktatas',
      megrendelok: 'Megrendelok',
      munkaszamok: 'Munkaszamok',
    };

    const endpoint = selectedMenu ? editableEndpointMap[selectedMenu] : undefined;

    if (endpoint) {
      try {
        const response = await apiService.get(`/${endpoint}/${row.id}`);
        const mergePreferNonEmpty = (base: any, incoming: any) => {
          const merged = { ...base, ...(incoming || {}) };

          Object.keys(base || {}).forEach((key) => {
            const baseValue = base?.[key];
            const incomingValue = incoming?.[key];

            if (
              typeof baseValue === 'string' &&
              baseValue.trim() !== '' &&
              typeof incomingValue === 'string' &&
              incomingValue.trim() === ''
            ) {
              merged[key] = baseValue;
            }
          });

          return merged;
        };

        const fullItem = response?.data ? mergePreferNonEmpty(row, response.data) : row;
        setSelectedItem(fullItem);
      } catch (err) {
        console.error(`Hiba a teljes rekord lekérése során (${endpoint}):`, err);
        setSelectedItem(row);
      }
      setIsPanelOpen(true);
      return;
    }

    setSelectedItem(row);
    setIsPanelOpen(true);
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setTimeout(() => setSelectedItem(null), 500); // Delayed to allow animation
  };

  const handleEdit = async (updatedData: any) => {
    try {
      // Call API to update data
      const savedRecord = await apiService.updateData(selectedMenu!, updatedData);

      if (selectedMenu) {
        await fetchData(selectedMenu);
      }

      setSelectedItem(savedRecord || updatedData);
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Sikeres frissítés!',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Hiba a frissítés során:', err);
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, 'Hiba történt a frissítés során.'),
        severity: 'error',
      });
    }
  };

  const handleDelete = async () => {
    try {
      // Call API to delete data
      await apiService.deleteData(selectedMenu!, selectedItem);
      
      // Remove from local data
      const updatedDataList = data.filter(item => item.id !== selectedItem.id);
      setData(updatedDataList);
      
      handlePanelClose();
      
      // Show success message
      setSnackbar({
        open: true,
        message: 'Sikeres törlés!',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Hiba a törlés során:', err);
      setSnackbar({
        open: true,
        message: getApiErrorMessage(err, 'Hiba történt a törlés során.'),
        severity: 'error',
      });
    }
  };

  const getEditFieldSuggestions = async (field: string, query: string, panelDataType?: string): Promise<string[]> => {
    if (!query || query.trim().length < 2) {
      return [];
    }

    const normalizedQuery = query.toLowerCase();
    const toUnique = (values: string[]) => Array.from(new Set(values.filter(v => !!v && v.trim().length > 0))).slice(0, 10);

    const extractResponseData = (response: any): any[] => {
      if (Array.isArray(response?.data)) return response.data;
      if (Array.isArray(response)) return response;
      return [];
    };

    try {
      if (panelDataType === 'ajanlatok') {
        if (field === 'megrendelo') {
          const response = await apiService.get(`/Megrendelok/search?query=${encodeURIComponent(query)}`);
          const options = extractResponseData(response).map((item: any) => item.cegnev || item.Cegnev).filter(Boolean);
          return toUnique(options as string[]);
        }

        if (field === 'ugyintezo') {
          const response = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
          const options = extractResponseData(response)
            .map((item: any) => item.displayName || item.userName || item.DisplayName || item.UserName)
            .filter(Boolean);
          return toUnique(options as string[]);
        }
      }

      if (panelDataType === 'iktatas') {
        if (field === 'munkaszam') {
          const response = await apiService.getData('munkaszamok');
          const options = response
            .filter((item: any) =>
              (item.munkaszam || '').toLowerCase().includes(normalizedQuery) ||
              (item.munkaTargya || '').toLowerCase().includes(normalizedQuery)
            )
            .map((item: any) => item.munkaszam)
            .filter(Boolean);
          return toUnique(options as string[]);
        }

        if (field === 'ugyintezo') {
          const response = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
          const options = extractResponseData(response)
            .map((item: any) => item.displayName || item.userName || item.DisplayName || item.UserName)
            .filter(Boolean);
          return toUnique(options as string[]);
        }

        if (field === 'cimzettKuldo') {
          const usersResponse = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
          const megrendelokResponse = await apiService.get(`/Megrendelok/search?query=${encodeURIComponent(query)}`);

          const userOptions = extractResponseData(usersResponse)
            .map((item: any) => item.displayName || item.userName || item.DisplayName || item.UserName)
            .filter(Boolean);
          const megrendeloOptions = extractResponseData(megrendelokResponse)
            .map((item: any) => item.cegnev || item.Cegnev)
            .filter(Boolean);

          return toUnique([...(userOptions as string[]), ...(megrendeloOptions as string[])]);
        }
      }

      if (panelDataType === 'munkaszamok') {
        if (field === 'megrendelo') {
          const response = await apiService.get(`/Megrendelok/search?query=${encodeURIComponent(query)}`);
          const options = extractResponseData(response).map((item: any) => item.cegnev || item.Cegnev).filter(Boolean);
          return toUnique(options as string[]);
        }

        if (field === 'ugyintezo' || field === 'resztvevok') {
          const response = await apiService.get(`/Users/search?query=${encodeURIComponent(query)}`);
          const options = extractResponseData(response)
            .map((item: any) => item.displayName || item.userName || item.DisplayName || item.UserName)
            .filter(Boolean);
          return toUnique(options as string[]);
        }
      }

      const localFallback = data
        .map((item: any) => item[field])
        .filter((value: any) => value != null)
        .map((value: any) => String(value))
        .filter((value: string) => value.toLowerCase().includes(normalizedQuery));

      return toUnique(localFallback);
    } catch (error) {
      console.error('Hiba az edit autocomplete javaslatok lekérése során:', error);
      return [];
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };


  // Magyar oszlopnevek formázása
  const formatColumnName = (key: string): string => {
    const translations: { [key: string]: string } = {
      'ajanlatiSzam': 'Ajánlati szám',
      'ajanlatTargya': 'Ajánlat tárgya',
      'valaszDatum': 'Válasz dátum',
      'iktatasIranya': 'Iktatás iránya',
      'munkaTargya': 'Munka tárgya',
      'iktatasTipusa': 'Iktatás típusa',
      'iratTargya': 'Irat tárgya',
      'cimzettKuldo': 'Címzett',
      'iratHelye': 'Irat helye',
      'cegnev': 'Cégnév',
      'iranyitoszam': 'Irányítószám',
      'megrendeloNeve': 'Megrendelő neve',
      'ev': 'Év',
      'sorszam': 'Sorszám',
      'datum': 'Dátum',
      'torzsszam': 'Törzsszám',
      'megrendelo': 'Megrendelő',
      'ugyintezo': 'Ügyintéző',
      'valasz': 'Válasz',
      'irsz': 'Irsz.',
      'cim': 'Cím',
      'cegjegyzekszam': 'Cégjegyzékszám',
      'adoszam': 'Adószám',
      'bankszamlaszam': 'Bankszámlaszám',
      'kepviselo': 'Képviselő',
      'munkaszam': 'Munkaszám',
      'resztvevok': 'Résztvevők',
      'projektKezdete': 'Projekt kezdete',
      'tervezettBefejezes': 'Tervezett befejezés',
      'iktatoszam': 'Iktatószám',
      'utolsoIdobejegjzes': 'Utolsó időbejegyzés',
      'osszesIdo': 'Összes idő (felh. idő)',
    };
    
    return translations[key] || key.charAt(0).toUpperCase() + key.slice(1);
  };

  // Dátum formázása csak év/hónap/nap formátumra
  const formatDate = (value: any): string => {
    if (!value) return '-';
    
    try {
      const date = new Date(value);
      if (isNaN(date.getTime())) return String(value);
      
      const year = date.getFullYear();
      const month = padLeft(date.getMonth() + 1, 2);
      const day = padLeft(date.getDate(), 2);
      
      return `${year}/${month}/${day}`;
    } catch {
      return String(value);
    }
  };

  // Érték formázása (ha dátum, akkor formázva)
  const formatCellValue = (key: string, value: any): string => {
    if (value === null || value === undefined) return '-';
    
    if (isDateFieldKey(key)) {
      return formatDate(value);
    }
    
    return String(value);
  };

  const getDetailPanelColumns = (): string[] => {
    const allColumns = getAllColumnsFromData(filteredData);
    const columnsToShow = getEffectiveColumns(allColumns)
      .filter((key) => hasColumn(allColumns, key));

    if (!selectedItem) {
      return columnsToShow;
    }

    const mergedColumns = [...columnsToShow];
    Object.keys(selectedItem).forEach((key) => {
      if (DETAIL_ONLY_HIDDEN_KEYS.has(key)) {
        return;
      }

      if (mergedColumns.indexOf(key) === -1) {
        mergedColumns.push(key);
      }
    });

    return mergedColumns;
  };

  // Calculate pagination
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentPageData = filteredData.slice(startIndex, endIndex);

  const getAllColumnsFromData = (rows: any[]): string[] => {
    return collectAllColumns(rows);
  };

  const getEffectiveColumns = (allColumns: string[]): string[] => {
    const defaultColumns = allColumns.filter((key) => key !== 'id');

    if (visibleColumns === undefined || visibleColumns === null) {
      return defaultColumns;
    }

    const filteredVisibleColumns = visibleColumns.filter(
      (key) => hasColumn(allColumns, key) && key !== 'id'
    );

    if (filteredVisibleColumns.length === 0 && defaultColumns.length > 0) {
      return defaultColumns;
    }

    return filteredVisibleColumns;
  };

  const renderTableHeaders = (data: any[]) => {
    if (data.length === 0) return null;
    const allColumns = getAllColumnsFromData(data);
    const columnsToShow = getEffectiveColumns(allColumns);
    
    return columnsToShow
      .filter(key => hasColumn(allColumns, key))
      .map((key) => (
        <TableCell key={key} sx={{ fontWeight: 'bold' }}>
          {formatColumnName(key)}
        </TableCell>
      ));
  };

  const renderTableRows = (data: any[]) => {
    if (data.length === 0) return null;
    const allColumns = getAllColumnsFromData(data);
    const columnsToShow = getEffectiveColumns(allColumns);
    
    return data.map((row, index) => (
      <TableRow 
        key={startIndex + index}
        onClick={() => handleRowClick(row)}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          transition: 'background-color 0.2s ease',
        }}
      >
        {columnsToShow
          .filter(key => hasColumn(allColumns, key))
          .map((key) => (
            <TableCell key={key}>
              {formatCellValue(key, row[key])}
            </TableCell>
          ))}
      </TableRow>
    ));
  };

  if (!selectedMenu) {
    return (
      <Box
        sx={{
          flexGrow: 1,
          backgroundColor: '#f0f3f6',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" color="text.secondary">
          Válassz egy menüpontot a bal oldali sávból
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flexGrow: 1,
        minWidth: 0, // Prevents flex item from growing beyond container
        overflow: 'auto', // Allows scrolling within the content area
        transition: 'margin-right 0.5s ease-in-out',
        marginRight: isPanelOpen ? '520px' : '0',
        position: 'relative',
      }}
    >
      <Card sx={{ backgroundColor: 'white' }}>
        <CardContent>
          {/* Header with title and records per page buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" component="h2">
              {getMenuTitle(selectedMenu)}
            </Typography>
            
            {!loading && !error && data.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Rekordok száma oldalanként:
                </Typography>
                <ButtonGroup variant="outlined" size="small">
                  <Button
                    variant={recordsPerPage === 50 ? 'contained' : 'outlined'}
                    onClick={() => handleRecordsPerPageChange(50)}
                  >
                    50
                  </Button>
                  <Button
                    variant={recordsPerPage === 100 ? 'contained' : 'outlined'}
                    onClick={() => handleRecordsPerPageChange(100)}
                  >
                    100
                  </Button>
                  <Button
                    variant={recordsPerPage === 200 ? 'contained' : 'outlined'}
                    onClick={() => handleRecordsPerPageChange(200)}
                  >
                    200
                  </Button>
                </ButtonGroup>
              </Box>
            )}
          </Box>
          
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          
          {!loading && !error && data.length > 0 && (
            <>
              <TableContainer component={Paper} sx={{ mt: 2, overflow: 'auto', maxWidth: '100%' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {renderTableHeaders(currentPageData)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renderTableRows(currentPageData)}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination and info */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Összesen {filteredData.length} rekord{searchFilter ? ` (${data.length} rekordból szűrve)` : ''}, {startIndex + 1}-{Math.min(endIndex, filteredData.length)} megjelenítve
                </Typography>
                
                {totalPages > 1 && (
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size="small"
                  />
                )}
              </Box>
            </>
          )}
          
          {!loading && !error && data.length > 0 && filteredData.length === 0 && (
            <Typography variant="body1" sx={{ mt: 2 }} color="text.secondary">
              Nincsenek találatok a keresési feltételnek megfelelően
            </Typography>
          )}
          
          {!loading && !error && data.length === 0 && (
            <Typography variant="body1" sx={{ mt: 2 }}>
              Nincsenek adatok
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Detail Panel */}
      <DetailPanel
        isOpen={isPanelOpen}
        data={selectedItem}
        columns={selectedItem && selectedMenu ? getDetailPanelColumns() : []}
        onClose={handlePanelClose}
        onEdit={handleEdit}
        onDelete={handleDelete}
        formatColumnName={formatColumnName}
        formatCellValue={formatCellValue}
        editPermission={editPermission}
        deletePermission={deletePermission}
        dataType={selectedMenu || undefined}
        onNavigateToParticipantTimeTracking={onNavigateToParticipantTimeTracking}
        onNavigateToFolyamatlap={onNavigateToFolyamatlap}
        onGetFieldSuggestions={getEditFieldSuggestions}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MainContent;
